import { Calendar, CalendarEvents, stringToDate } from "@dhx/ts-calendar";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { create, el } from "@dhx/ts-common/dom";
import { Label } from "./helper/label";
import { Popup } from "@dhx/ts-popup";
import { ValidationStatus, ICalendarInput, IBaseDatepickerEventHandlersMap, BaseElementEvent } from "../types";
import { getFormItemCss, getValidationMessage, isEmptyObj } from "../helper";


export class DateInput extends Label  {
	public config: ICalendarInput;
	public calendar: Calendar;
	public events: IEventSystem<BaseElementEvent, IBaseDatepickerEventHandlersMap> = new EventSystem();
	private _popup: Popup;

	constructor(container, config: ICalendarInput) {
		super(null, config);

		this.setConfig(config);
		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	validate(): boolean {
		const { required, value, validation } = this.config;
		let isValid = true;
		isValid = validation
			? this.config.validation(value)
			: !required || Boolean(value);
		this.config.$validationStatus = isValid
			? ValidationStatus.success
			: ValidationStatus.error;
		this.paint();
		return isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	setValue(value: string | Date): void {
		this.calendar.setValue(value);
		this.paint();
	}
	setConfig(config: ICalendarInput): void {
		if (isEmptyObj(config)) {
			return;
		}
		if (this.calendar) {
			this.calendar.destructor();
		}
		if (this._popup) {
			this._popup.destructor();
		}
		this.config = {
			type: this.config.type,
			id: this.config.id,
			name: this.config.name,
			value: ""
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}

		this._popup = new Popup({css: "dhx_widget--border-shadow"});
		this.calendar = new Calendar(null, config);
		this._popup.attach(this.calendar);

		if (this.config.value) {
			this.calendar.setValue(this.config.value);
			this.config.value = this.calendar.getValue();
		}
		this.calendar.events.on(CalendarEvents.change, () => {
			this.config.value = this.calendar.getValue() as string;
			this._popup.hide();
			this.validate();
		});
		this.events.on(BaseElementEvent.change, value => {
			this.config.value = this._inputValidate(value);
			if (this._inputValidate(value)) {
				this.calendar.setValue(value);
			} else {
				this.validate();
			}
		});

		this.events.fire(BaseElementEvent.configUpdate, [this.config]);
		this.clearValidate();
	}
	getValue(): string {
		return this.config.value || "";
	}
	clear(): void {
		this.config.value = "";
		this.paint();
	}
	getWidget(): Calendar {
		return this.calendar;
	}
	protected _getHandlers() {
		return {
			onfocus: () => {
				if (this._popup.isVisible()) {
					return;
				}
				const node = this.getRootView().refs.input.el;
				this._popup.show(node);
			},
			onchange:(e: Event) => {
				const value = (e.target as HTMLInputElement).value;
				this.events.fire(BaseElementEvent.change, [value]);
			},
			onkeyup: (e: KeyboardEvent) => {
				if (e.keyCode === 13) {
					if (this._popup.isVisible()) {
						this._popup.hide();
					}
					const node = this.getRootView().refs.input.el;
					node.blur();
				}
			}
		};
	}
	protected _inputValidate(value: string): string {
		const dateFormat = this.calendar.config.dateFormat;
		return stringToDate(value, dateFormat, true) ? value : "";
	}
	protected _draw() {
		const { value, icon, required, disabled, placeholder, name, id, validation, editing = false } = this.config;
		return el("div.dhx_form-group", {
			class: getFormItemCss(this.config, Boolean(required) || Boolean(validation)),
		}, [
			this._drawLabel(),
			el(".dhx_input__wrapper", [
				el("div.dhx_input__container", {
				}, [
					el(".dhx_input__icon", {
						class: icon || "dxi dxi-calendar-today"
					}),
					el("input.dhx_input.dhx_input--icon-padding", {
						_key: this._uid,
						value,
						type: "text",
						_ref: "input",
						required,
						disabled,
						placeholder: placeholder || "",
						name: name || "",
						id: id || this._uid,
						onfocus: this._handlers.onfocus,
						onchange: this._handlers.onchange,
						onkeyup: this._handlers.onkeyup,
						autocomplete: "off",
						readOnly: !editing
					}),
				]),
				getValidationMessage(this.config) && el("span.dhx_input__caption", {
				}, getValidationMessage(this.config))
			]),
		]);
	}
}
